/*!
 * Copyright (c) Trimble Inc. All rights reserved
 */

import { Injectable } from '@angular/core';
import { IConvertOptionsFacade } from '../../../../../data-access/src/lib/domain/facades-models/convert-options-facade-interface';
import { BackendService, LocalStorageService } from '../../../../../utils/src';
import { GlobalConstants } from '../../constants/global-constants';
import { ConvertSystemInfo } from '../../models/convert-system-info';
import { ConvertOptionReturnCodes } from '../../models/place-coupling-return-codes';
import { ConvertOptionState } from '../../models/convert-option-state';
import { ReceiveMessageHandler } from '@mep/web-browser-bridge';
import { PlaceCouplingReceiveController } from '../../receive-controllers/place-coupling-receive-controller';
import { from, Observable, take } from 'rxjs';

@Injectable({
  providedIn: 'root',
})
export class ConvertOptionsFacade implements IConvertOptionsFacade {
  constructor(
    public backendService: BackendService,
    public localStorageService: LocalStorageService,
    public readonly receiveMessageHandler: ReceiveMessageHandler
  ) {}

  public changeHeight(height: number): void {
    this.backendService.changeDialogHeight(height).pipe(take(1)).subscribe();
  }

  public saveData(convertSystemInfo: ConvertSystemInfo): Observable<void> {
    return from(
      this.backendService.messageHandler.sendRequestAsync<void>(GlobalConstants.ApplyBtnClick, [convertSystemInfo])
    );
  }

  public updateRetainValue(value: boolean): Observable<void> {
    return from(this.backendService.messageHandler.sendRequestAsync<void>(GlobalConstants.UpdateRetainValue, [value]));
  }

  public updateIsIgnoreValue(value: boolean): Observable<void> {
    return from(
      this.backendService.messageHandler.sendRequestAsync<void>(GlobalConstants.UpdateIsIgnoreValue, [value])
    );
  }

  public getInitialData(): Observable<ConvertSystemInfo> {
    return from<Promise<ConvertSystemInfo>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.InitializeData)
    );
  }

  public closeWindow(): void {
    this.backendService.messageHandler.sendRequestAsync(GlobalConstants.CloseBtnClick);
  }

  public validateSelection(convertOptionState: ConvertOptionState): Observable<ConvertOptionReturnCodes> {
    return from<Promise<ConvertOptionReturnCodes>>(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.ValidateSelection, [convertOptionState])
    );
  }

  public registerControllers(): void {
    this.receiveMessageHandler.registerController(new PlaceCouplingReceiveController(this.localStorageService));
  }

  public updateLocalStorage(key: string, value: any): void {
    this.localStorageService.updateStorage(key, value);
  }

  public getLocalStorage(key: string): any {
    return this.localStorageService.getStorage(key);
  }

  public updateCouplingAlignment(value: boolean): Observable<any> {
    return from(
      this.backendService.messageHandler.sendRequestAsync(GlobalConstants.UpdateCouplingAlignmentValue, [value])
    );
  }
}
